/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Rank what one class needs retaught next, from the marked answers of its papers.
 * @state-owner none; callers pass dataset records in
 * @allowed-imports shared view types, demo constants and scoring selectors
 * @forbidden React; network; store implementations; UI strings beyond dataset labels
 * @public-api classrooms next-lesson tab
 * @side-effects none
 * @tests modules/classrooms/model/next-lesson.test.ts
 */

import { CONCEPTS, MISCONCEPTIONS, QUESTIONS } from '../../../shared/lib/demo-data';
import type { AnswerMark, ClassRecord, RunRecord } from '../../../shared/lib/model';
import { classRuns } from '../../../shared/lib/selectors';
import type { AnswerSheet } from '../../../shared/model/dataset-store';
import { answerKey } from '../../../shared/model/dataset-store';

function passed(mark: AnswerMark): boolean {
  return mark === 'ok' || mark === 'fix';
}

/** One paper's result for one concept. */
export interface ConceptPoint {
  readonly runId: string;
  readonly label: string;
  readonly percent: number;
  readonly questions: number;
}

export interface ConceptTrend {
  readonly concept: string;
  readonly points: readonly ConceptPoint[];
  readonly latestPercent: number;
  /** A concept only trends when at least two papers asked about it. */
  readonly comparable: boolean;
}

export interface Recommendation {
  readonly concept: string;
  /** Students who failed this concept in every paper that asked about it. */
  readonly students: readonly string[];
  readonly papers: number;
  readonly questions: number;
  /** Change in class correct rate from the previous paper to the latest; negative means slipping. */
  readonly deltaPercent: number;
  readonly trend: readonly ConceptPoint[];
  /** Concepts that build on this one, which is why it is worth fixing first. */
  readonly prerequisiteOf: readonly string[];
  readonly latestPercent: number;
  readonly score: number;
}

export interface ErrorPattern {
  readonly misconceptionId: string;
  readonly label: string;
  readonly concept: string;
  readonly students: readonly string[];
}

export interface NextLesson {
  readonly rosterSize: number;
  /** Papers with submitted work; a session still running is not one yet. */
  readonly papers: number;
  readonly questions: number;
  readonly answers: number;
  readonly comparableQuestions: number;
  readonly recommendations: readonly Recommendation[];
  readonly patterns: readonly ErrorPattern[];
  readonly trends: readonly ConceptTrend[];
  readonly mastered: readonly ConceptTrend[];
}

export interface NextLessonInput {
  readonly classIndex: number;
  readonly classes: readonly ClassRecord[];
  readonly runs: readonly RunRecord[];
  readonly answers: Readonly<Record<string, AnswerSheet>>;
}

const MASTERED_AT = 80;
const RECOMMENDATION_LIMIT = 3;

function percentOf(part: number, whole: number): number {
  return whole ? Math.round((part / whole) * 100) : 0;
}

/**
 * `RUNS` is authored newest first, so reversing a class's runs puts its papers in the order the
 * students actually sat them. Live sessions are excluded: nothing has been handed in yet.
 */
function papersOf(runs: readonly RunRecord[], classIndex: number): readonly RunRecord[] {
  return [...classRuns(runs, classIndex)].filter((run) => run.status !== 'live').reverse();
}

export function nextLesson(input: NextLessonInput): NextLesson {
  const { answers, classes, classIndex, runs } = input;
  const roster = classes[classIndex]?.students ?? [];
  const papers = papersOf(runs, classIndex);

  /** marks[runId][questionIndex][student] — every judgement, keyed the way the figures read it. */
  const byPaper = papers.map((run) => ({
    run,
    questions: QUESTIONS.map((_, index) =>
      roster
        .map((student) => ({ student, mark: answers[answerKey(run.id, student)]?.marks[index] }))
        .filter((entry): entry is { student: string; mark: AnswerMark } => Boolean(entry.mark)),
    ),
  }));

  const answered = byPaper.reduce(
    (sum, paper) => sum + paper.questions.reduce((count, entries) => count + entries.length, 0),
    0,
  );

  const conceptNames = [...new Set(QUESTIONS.map((question) => question.concept))];

  const trends: ConceptTrend[] = conceptNames.map((concept) => {
    const indexes = QUESTIONS.map((question, index) => ({ question, index })).filter(
      (entry) => entry.question.concept === concept,
    );
    const points = byPaper
      .map(({ questions, run }) => {
        const entries = indexes.flatMap((entry) => questions[entry.index]);
        return {
          runId: run.id,
          label: run.dateLabel,
          percent: percentOf(entries.filter((entry) => passed(entry.mark)).length, entries.length),
          questions: indexes.length,
        };
      })
      .filter((point) => point.questions > 0);
    return {
      concept,
      points,
      latestPercent: points[points.length - 1]?.percent ?? 0,
      comparable: points.length >= 2,
    };
  });

  /** How many questions can be read across papers at all, which bounds every trend claim. */
  const comparableQuestions = trends
    .filter((trend) => trend.comparable)
    .reduce((sum, trend) => sum + (trend.points[0]?.questions ?? 0), 0);

  const recommendations = trends
    .filter((trend) => trend.points.length > 0 && trend.latestPercent < MASTERED_AT)
    .map((trend) => {
      const indexes = QUESTIONS.map((question, index) => ({ question, index })).filter(
        (entry) => entry.question.concept === trend.concept,
      );
      /** Missed it in every paper that asked — one bad day does not make a reteaching case. */
      const students = roster.filter((student) =>
        byPaper.every(({ questions }) =>
          indexes.some((entry) =>
            questions[entry.index].some(
              (result) => result.student === student && !passed(result.mark),
            ),
          ),
        ),
      );
      const previous = trend.points[trend.points.length - 2]?.percent ?? trend.latestPercent;
      const deltaPercent = trend.latestPercent - previous;
      const prerequisiteOf =
        CONCEPTS.find((concept) => concept.id === trend.concept)?.prerequisiteOf ?? [];
      return {
        concept: trend.concept,
        students,
        papers: trend.points.length,
        questions: indexes.length,
        deltaPercent,
        trend: trend.points,
        prerequisiteOf,
        latestPercent: trend.latestPercent,
        /** Reach × how fast it is slipping × what else is blocked behind it. */
        score:
          students.length * (1 + Math.max(0, -deltaPercent) / 100) * (1 + prerequisiteOf.length),
      };
    })
    .filter((recommendation) => recommendation.students.length > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, RECOMMENDATION_LIMIT);

  const patterns = MISCONCEPTIONS.map((misconception) => {
    const indexes = QUESTIONS.map((question, index) => ({ question, index })).filter(
      (entry) => entry.question.misconceptionId === misconception.id,
    );
    const students = roster.filter((student) =>
      byPaper.some(({ questions }) =>
        indexes.some((entry) =>
          questions[entry.index].some(
            (result) => result.student === student && !passed(result.mark),
          ),
        ),
      ),
    );
    return {
      misconceptionId: misconception.id,
      label: misconception.label,
      concept: misconception.concept,
      students,
    };
  })
    .filter((pattern) => pattern.students.length > 0)
    .sort((a, b) => b.students.length - a.students.length);

  return {
    rosterSize: roster.length,
    papers: papers.length,
    questions: QUESTIONS.length * papers.length,
    answers: answered,
    comparableQuestions,
    recommendations,
    patterns,
    trends: trends.filter((trend) => trend.latestPercent < MASTERED_AT),
    mastered: trends.filter((trend) => trend.latestPercent >= MASTERED_AT),
  };
}

/** The text the summary button produces, built from the same numbers shown on the cards. */
export function nextLessonSummary(lesson: NextLesson): string {
  if (!lesson.recommendations.length) {
    return '這個班沒有在多次評量上重複卡住的概念，下一堂可以照原訂進度走。';
  }
  const lines = lesson.recommendations.map(
    (item) =>
      `${item.concept}：${item.students.length} 人在 ${item.papers} 次評量都沒通過` +
      (item.deltaPercent < 0 ? `，較上次退步 ${Math.abs(item.deltaPercent)}%` : '') +
      (item.prerequisiteOf.length ? `，${item.prerequisiteOf.join('、')}會卡在這裡` : ''),
  );
  const top = lesson.patterns[0];
  if (top) lines.push(`最常見的錯法是「${top.label}」，${top.students.length} 人出現過。`);
  return lines.join('\n');
}
